<template>
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card">
          <div>
            <div class="login-main wide-main position-relative">
              <div v-if="loading" class="loader-overlay d-flex align-items-center justify-content-center">
                <div class="text-center">
                  <div class="spinner-border text-primary" role="status" aria-label="Loading"></div>
                  <div class="mt-2 fw-semibold text-primary">Please wait...</div>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-sm-3 mb-3">
                <h2 class="mb-0">My Subscription</h2>
              </div>

              <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4">
                <div class="d-flex align-items-center gap-3 flex-wrap">
                  <img
                      :src="`/api/driver/${driverId}/profile-picture`"
                      alt="Driver photo"
                      class="rounded-circle border"
                      style="width: 84px; height: 84px; object-fit: cover;"
                  />
                  <div class="flex-grow-1">
                    <div class="fs-4 f-w-700">{{ driver.name }}</div>
                    <div class="text-muted" v-if="driver.rating !== null">
                      <i class="fa-solid fa-star text-warning"></i>
                      <span class="ms-1">{{ Number(driver.rating).toFixed(1) }}</span>
                    </div>
                  </div>
                </div>
                <button
                    v-if="hasSubscription"
                    class="btn btn-primary"
                    @click="openBillingPortal"
                    :disabled="actionLoading"
                >
                  <span v-if="actionLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <i v-else class="fa-solid fa-gear me-2"></i>
                  Manage Subscription
                </button>
              </div>

              <hr/>

              <div class="subscription-summary-card mb-4 p-3 p-sm-4 rounded-3 border bg-light">
                <div class="row align-items-center">
                  <div class="col-md-6">
                    <h5 class="text-muted mb-1">Current Plan</h5>
                    <div class="fs-3 fw-bold text-primary">
                      {{ hasSubscription ? currentPlanName : 'No Active Plan' }}
                    </div>
                    <span v-if="isTrialing" class="badge bg-info mt-1">Free Trial</span>
                    <span v-else-if="subscriptionStatus === 'ACTIVE'" class="badge bg-success mt-1">Active</span>
                    <span v-else-if="subscriptionStatus === 'CANCELLED'" class="badge bg-warning mt-1">Cancelled</span>
                    <span v-else class="badge bg-secondary mt-1">Inactive</span>
                  </div>
                  <!-- Shown in every state, trial included: dispatch gates on rideCredits > 0 alone
                       (DriverFinderService.findAvailableDrivers), so the balance is what decides
                       whether this driver receives ride requests regardless of subscription status. -->
                  <div class="col-md-6 text-md-end mt-3 mt-md-0">
                    <h5 class="text-muted mb-1">Available Credits</h5>
                    <div class="fs-1 fw-bold" :class="creditCountClass">
                      <i class="fa-solid fa-coins me-2"></i>{{ rideCredits }}
                    </div>
                    <small class="text-muted" v-if="weeklyCreditAllowance">
                      of {{ weeklyCreditAllowance }} granted {{ isTrialing ? 'each week of your trial' : 'each billing period' }}
                    </small>
                    <small class="text-muted" v-else>ride credits remaining</small>
                  </div>
                </div>

                <div v-if="isTrialing && trialEndDate" class="mt-3 pt-3 border-top">
                  <div class="d-flex align-items-center gap-2">
                    <i class="fa-solid fa-hourglass-half text-info"></i>
                    <div>
                      <small class="text-muted">Free trial ends:</small>
                      <span class="ms-2 fw-semibold text-info">{{ formatDate(trialEndDate) }}</span>
                      <span class="ms-2 text-muted small">({{ trialDaysLeft }} days left — no charge until then)</span>
                    </div>
                  </div>
                </div>

                <div v-if="hasSubscription && nextCreditGrantAt" class="mt-3 pt-3 border-top">
                  <div class="d-flex align-items-center gap-2">
                    <i class="fa-solid fa-rotate text-primary"></i>
                    <div>
                      <small class="text-muted">Next credit grant:</small>
                      <span class="ms-2 fw-semibold text-black">{{ formatDate(nextCreditGrantAt) }}</span>
                      <span v-if="weeklyCreditAllowance" class="ms-2 text-muted small">
                        (+{{ weeklyCreditAllowance }} credits)
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="hasSubscription && rideCredits <= 0" class="alert alert-danger mt-3 mb-0">
                  <i class="fa-solid fa-triangle-exclamation me-2"></i>
                  <strong>You have no ride credits left.</strong>
                  You will not receive new ride requests until
                  <template v-if="nextCreditGrantAt">your credits renew on {{ formatDate(nextCreditGrantAt) }}</template>
                  <template v-else>your credits renew</template><template v-if="availableAddOns.length"> — or buy a top-up below</template>.
                </div>
                <div v-else-if="hasSubscription && rideCredits <= lowCreditThreshold" class="alert alert-warning mt-3 mb-0">
                  <i class="fa-solid fa-battery-quarter me-2"></i>
                  <strong>{{ rideCredits }} ride {{ rideCredits === 1 ? 'credit' : 'credits' }} left.</strong>
                  Each accepted ride uses one credit
                  <template v-if="nextCreditGrantAt">, and yours renew on {{ formatDate(nextCreditGrantAt) }}</template>.
                </div>

                <div v-if="subscriptionStatus === 'ACTIVE' && subscriptionEndDate" class="mt-3 pt-3 border-top">
                  <div>
                    <small class="text-muted">Next billing date:</small>
                    <span class="ms-2 fw-semibold text-black">{{ formatDate(subscriptionEndDate) }}</span>
                  </div>
                </div>

                <div v-if="subscriptionStatus === 'CANCELLED' && gracePeriodDaysLeft > 0" class="alert alert-warning mt-3 mb-0">
                  <i class="fa-solid fa-clock me-2"></i>
                  Your subscription was cancelled. You have <strong>{{ gracePeriodDaysLeft }} days</strong> left in your grace period.
                  Resubscribe now to keep your credits!
                </div>

                <div v-if="hasPendingChange" class="alert alert-info mt-3 mb-0">
                  <i class="fa-solid fa-clock-rotate-left me-2"></i>
                  <strong>Pending Plan Change:</strong> Your subscription will change to
                  <strong>{{ pendingPlanName || pendingTierKey }}</strong> on
                  <strong>{{ formatDate(pendingChangeEffectiveDate) }}</strong>.
                  <br/>
                  <small class="text-muted">Your credits will remain unchanged until the new billing cycle begins.</small>
                </div>
              </div>

              <div v-if="!hasSubscription">
                <h4 class="mb-3">
                  <i class="fa-solid fa-layer-group me-2"></i>
                  Choose a Plan
                </h4>

                <div class="row g-3 mb-4">
                  <div v-for="plan in subscriptionPlans" :key="plan.id" class="col-lg-4 col-md-6">
                    <div class="card h-100 subscription-plan-card">
                      <div class="card-header text-center py-3" :class="getPlanHeaderClass(plan)">
                        <h5 class="card-title mb-0 text-white">{{ plan.name }}</h5>
                        <span
                            v-if="plan.trialAvailable && !hasUsedFreeTrial"
                            class="plan-trial-badge mt-2"
                        ><i class="fa-solid fa-gift me-1"></i>{{ plan.trialPeriodDays }}-Day Free Trial</span>
                      </div>
                      <div class="card-body text-center">
                        <div class="price-display mb-3">
                          <span class="fs-1 fw-bold">${{ plan.priceInDollars }}</span>
                          <span class="text-muted">/{{ plan.billingInterval || 'week' }}</span>
                        </div>
                        <ul class="list-unstyled text-start mb-4">
                          <li class="mb-2">
                            <i class="fa-solid fa-check text-success me-2"></i>
                            <strong>{{ plan.rideCredits }}</strong> ride credits
                          </li>
                          <li class="mb-2">
                            <i class="fa-solid fa-check text-success me-2"></i>
                            Keep <strong>95%</strong> of your earnings
                          </li>
                          <li class="mb-2" v-if="plan.rolloverPercentage">
                            <i class="fa-solid fa-check text-success me-2"></i>
                            {{ plan.rolloverPercentage }}% credit rollover
                          </li>
                          <li class="mb-2" v-if="plan.costPerRide != null">
                            <i class="fa-solid fa-check text-success me-2"></i>
                            <strong>${{ Number(plan.costPerRide).toFixed(2) }}</strong> per ride
                          </li>
                          <li class="mb-2" v-if="plan.trialAvailable && !hasUsedFreeTrial">
                            <i class="fa-solid fa-gift text-info me-2"></i>
                            <strong>{{ plan.trialPeriodDays }} days</strong> free trial,
                            <strong>{{ plan.trialRideCredits }}</strong> ride credits per week
                          </li>
                          <li v-if="plan.description" class="text-muted small">
                            {{ plan.description }}
                          </li>
                        </ul>
                        <button
                            class="btn btn-primary w-100"
                            @click="selectPlan(plan)"
                            :disabled="actionLoading"
                        >
                          <span v-if="plan.trialAvailable && !hasUsedFreeTrial">
                            Start {{ plan.trialPeriodDays }}-Day Free Trial
                          </span>
                          <span v-else>Subscribe</span>
                        </button>
                        <small v-if="plan.trialAvailable && !hasUsedFreeTrial" class="text-muted d-block mt-2">
                          No charge for {{ plan.trialPeriodDays }} days. Cancel anytime.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="(subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'TRIALING') && availableAddOns.length > 0">
                <h4 class="mb-3">
                  <i class="fa-solid fa-plus-circle me-2"></i>
                  Need More Credits?
                </h4>
                <p class="text-muted mb-3">Purchase additional ride credits for your current plan.</p>

                <div class="row g-3 mb-4">
                  <div v-for="addon in availableAddOns" :key="addon.id" class="col-lg-3 col-md-4 col-6">
                    <div class="card h-100 addon-card text-center">
                      <div class="card-body">
                        <div class="addon-credits mb-2">
                          <span class="fs-3 fw-bold text-primary">+{{ addon.rideCredits }}</span>
                        </div>
                        <div class="text-muted mb-2">credits</div>
                        <div class="addon-price fs-5 fw-semibold mb-3">${{ addon.priceInDollars }}</div>
                        <button
                            class="btn btn-outline-primary btn-sm w-100"
                            @click="purchaseAddOn(addon)"
                            :disabled="actionLoading"
                        >
                          Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger mt-3">
                <i class="fa-solid fa-exclamation-circle me-2"></i>
                {{ error }}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Swal from "../../extras/js/sweetalert/sweetalert2.min";

const props = defineProps({
  driverId: { type: String, required: true },
  token: { type: String, required: true }
});

const loading = ref(true);
const actionLoading = ref(false);
const error = ref(null);

const driver = ref({
  name: null,
  rating: null
});

const rideCredits = ref(0);
const weeklyCreditAllowance = ref(null);
// Server-supplied (utils.DriverCreditConstants) so the threshold can be retuned without a release.
const lowCreditThreshold = ref(0);
const nextCreditGrantAt = ref(null);
const subscriptionStatus = ref(null);
const currentTierKey = ref(null);
const addOnTierKey = ref(null);
const subscriptionEndDate = ref(null);
const gracePeriodDaysLeft = ref(0);
const trialEndDate = ref(null);
const hasUsedFreeTrial = ref(false);
const subscriptionPlans = ref([]);
const allAddOns = ref([]);

const hasPendingChange = ref(false);
const pendingTierKey = ref(null);
const pendingPlanName = ref(null);
const pendingChangeEffectiveDate = ref(null);

const isTrialing = computed(() => subscriptionStatus.value === 'TRIALING');
const hasSubscription = computed(() => subscriptionStatus.value === 'ACTIVE' || subscriptionStatus.value === 'TRIALING');

const trialDaysLeft = computed(() => {
  if (!trialEndDate.value) return 0;
  const end = parseDate(trialEndDate.value);
  if (!end) return 0;
  const now = new Date();
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
});

// The trial is metered like any paid week (#180): 20 credits a week, not unlimited access. A driver
// at zero stops being dispatched whether they are trialing or paying, so the count is coloured the
// same way in both states.
const creditCountClass = computed(() => {
  if (rideCredits.value <= 0) return 'text-danger';
  if (rideCredits.value <= lowCreditThreshold.value) return 'text-warning';
  return 'text-success';
});

const currentPlanName = computed(() => {
  if (!currentTierKey.value) return 'None';
  const plan = subscriptionPlans.value.find(p => p.tierKey === currentTierKey.value);
  return plan ? plan.name : currentTierKey.value;
});

// Filters on the add-on's own tierKey against the key the server resolved, not on parentTierKey
// against currentTierKey: trial packs belong to the trial rather than to a plan, so they carry no
// parent at all and would never match (#298).
const availableAddOns = computed(() => {
  const canPurchase = subscriptionStatus.value === 'ACTIVE' || subscriptionStatus.value === 'TRIALING';
  if (!canPurchase || !addOnTierKey.value) return [];
  return allAddOns.value.filter(addon => addon.tierKey === addOnTierKey.value);
});

/**
 * Resolves the header colour for a plan tier.
 *
 * The theme's own `bg-secondary` and `bg-success` carry white text at roughly 2:1 — the plan name
 * was barely readable on them — so each tier keeps its hue in a shade that clears 4.5:1 instead of
 * reusing the utility class.
 *
 * @param plan the plan being rendered
 * @returns {string} the CSS class to apply to that plan's card header
 */
function getPlanHeaderClass(plan) {
  const tierClasses = {
    'starter': 'plan-header-starter',
    'full_time': 'plan-header-full-time',
    'ultimate': 'plan-header-ultimate'
  };
  return tierClasses[plan.tierKey] || 'plan-header-full-time';
}

function parseDate(dateStr) {
  if (!dateStr) return null;
  // PostgreSQL timestamptz arrives as "2026-05-03 00:48:42.000000 +00:00".
  // Normalize to ISO 8601 ("2026-05-03T00:48:42.000000+00:00") so Safari
  // doesn't return Invalid Date.
  const normalized = String(dateStr).replace(' ', 'T').replace(/\s([+-])/, '$1');
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = parseDate(dateStr);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

async function loadSubscriptionData() {
  loading.value = true;
  error.value = null;

  try {
    const response = await fetch(`/api/driver-subscription?token=${encodeURIComponent(props.token)}`);
    if (!response.ok) {
      throw new Error('Failed to load subscription data');
    }

    const data = await response.json();

    driver.value.name = data.driverName;
    driver.value.rating = data.driverRating;
    rideCredits.value = data.rideCredits || 0;
    weeklyCreditAllowance.value = data.weeklyCreditAllowance ?? null;
    lowCreditThreshold.value = data.lowCreditThreshold ?? 0;
    nextCreditGrantAt.value = data.nextCreditGrantAt || null;
    subscriptionStatus.value = data.subscriptionStatus;
    currentTierKey.value = data.currentTierKey;
    // Falls back to currentTierKey so a rolling deploy -- new page, XHR answered by an old instance
    // that does not emit addOnTierKey yet -- degrades to the previous behaviour instead of showing
    // every driver an empty pack list with no error.
    addOnTierKey.value = data.addOnTierKey || data.currentTierKey || null;
    subscriptionEndDate.value = data.subscriptionEndDate;
    gracePeriodDaysLeft.value = data.gracePeriodDaysLeft || 0;
    trialEndDate.value = data.trialEndDate || null;
    hasUsedFreeTrial.value = data.hasUsedFreeTrial || false;
    subscriptionPlans.value = data.plans || [];
    allAddOns.value = data.addOns || [];

    hasPendingChange.value = data.hasPendingChange || false;
    pendingTierKey.value = data.pendingTierKey || null;
    pendingPlanName.value = data.pendingPlanName || null;
    pendingChangeEffectiveDate.value = data.pendingChangeEffectiveDate || null;

  } catch (e) {
    error.value = e.message || 'An error occurred while loading subscription data';
  } finally {
    loading.value = false;
  }
}

async function selectPlan(plan, expectTrial = plan.trialAvailable && !hasUsedFreeTrial.value) {
  actionLoading.value = true;
  error.value = null;

  try {
    // expectTrial is what this card actually promised. Plans are fetched once on mount and never
    // refetched, so a scheduled promo end can close the window while the page sits open; sending it
    // lets the server refuse instead of quietly creating a session that charges today.
    const response = await fetch(`/api/driver-subscription/checkout?token=${encodeURIComponent(props.token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId: plan.id, expectTrial })
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.trialExpired) {
        const confirmed = await Swal.fire({
          icon: 'warning',
          title: 'Free trial ended',
          text: errorData.message,
          showCancelButton: true,
          confirmButtonText: `Continue at $${plan.priceInDollars}/${plan.billingInterval || 'week'}`,
          cancelButtonText: 'Not now'
        });
        actionLoading.value = false;
        if (confirmed.isConfirmed) {
          // Refresh first so the card stops advertising a trial that is gone.
          await loadSubscriptionData();
          return selectPlan(plan, false);
        }
        return;
      }
      throw new Error(errorData.message || 'Failed to create checkout session');
    }

    const data = await response.json();
    if (data.checkoutUrl) {
      window.location.href = data.checkoutUrl;
    }

  } catch (e) {
    error.value = e.message || 'Failed to start subscription process';
    Swal.fire({
      icon: 'error',
      title: 'Subscription Error',
      text: e.message || 'Failed to start subscription process'
    });
  } finally {
    actionLoading.value = false;
  }
}

async function purchaseAddOn(addon) {
  actionLoading.value = true;
  error.value = null;

  try {
    const response = await fetch(`/api/driver-subscription/addon-checkout?token=${encodeURIComponent(props.token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addonId: addon.id })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create add-on checkout session');
    }

    const data = await response.json();
    if (data.checkoutUrl) {
      window.location.href = data.checkoutUrl;
    }

  } catch (e) {
    error.value = e.message || 'Failed to purchase add-on';
    Swal.fire({
      icon: 'error',
      title: 'Purchase Error',
      text: e.message || 'Failed to purchase add-on'
    });
  } finally {
    actionLoading.value = false;
  }
}

async function openBillingPortal() {
  actionLoading.value = true;
  error.value = null;

  try {
    const response = await fetch(`/api/driver-subscription/billing-portal?token=${encodeURIComponent(props.token)}`, {
      method: 'POST'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to open billing portal');
    }

    const data = await response.json();
    if (data.portalUrl) {
      window.location.href = data.portalUrl;
    }

  } catch (e) {
    error.value = e.message || 'Failed to open billing portal';
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: e.message || 'Failed to open billing portal'
    });
  } finally {
    actionLoading.value = false;
  }
}

onMounted(() => {
  loadSubscriptionData();
});
</script>

<style scoped>
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 100;
  border-radius: inherit;
}

.subscription-summary-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.subscription-plan-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.subscription-plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.subscription-plan-card.selected {
  border-width: 3px;
  box-shadow: 0 4px 15px rgba(var(--bs-primary-rgb), 0.3);
}

.subscription-plan-card.pending {
  border-width: 2px;
  border-style: dashed;
  box-shadow: 0 4px 15px rgba(var(--bs-info-rgb), 0.2);
}

.addon-card {
  transition: transform 0.2s;
}

.addon-card:hover {
  transform: translateY(-2px);
}

.price-display {
  color: #333;
}

/* Tier hues at a depth that carries white text: the theme's #f39159 and #3eb95f are ~2:1 against
   white, which is what made the plan name hard to read. */
.plan-header-starter {
  background-color: #a9521c;
}

.plan-header-full-time {
  background-color: #2d348f;
}

.plan-header-ultimate {
  background-color: #2a7f3f;
}

/* Sits in the flow under the plan name rather than pinned to the corner, where it covered the name
   on a phone and clipped at the card edge. Solid white reads against every tier colour, which an
   orange badge on the orange starter header did not. */
.plan-trial-badge {
  display: inline-block;
  background-color: #fff;
  color: #212529;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  max-width: 100%;
  white-space: normal;
}

.wide-main {
  max-width: 1000px;
}

/* The theme pins .login-main to a fixed width — 90vw below 576px — and adds 20px of its own padding
   inside .login-card's 12px, so a 375px phone renders this page in a 298px column with the login
   background showing down both sides. `max-width` alone cannot undo an explicit `width`. This page
   is opened from the driver app, where a sheet that fills the webview is what the surrounding
   chrome looks like. */
@media (max-width: 575.98px) {
  /* Block rather than flex: the unclassed wrapper between .login-card and .login-main is a flex
     item, so it shrinks to its content and a full-width .login-main resolves against that instead
     of the viewport. */
  .login-card {
    display: block;
    padding: 0;
  }

  .login-card > div {
    width: 100%;
  }

  .login-card .login-main.wide-main {
    width: 100%;
    max-width: none;
    min-height: 100vh;
    padding: 16px 14px;
    border-radius: 0;
    box-shadow: none;
  }
}
</style>
